import { useEffect, useState } from 'react';
import { usePublish, type PublishPhase } from '../../lib/usePublish';
import PublishButton from './PublishButton';

interface ViewProps {
  /** La página lo pone en true tras guardar un cambio en la base de datos. */
  needsPublish: boolean;
  phase: PublishPhase;
  error: string | null;
  publish: () => Promise<void>;
  /** Se llama cuando la publicación se confirma, para que la página limpie needsPublish. */
  onPublished?: () => void;
  /** Mensaje a mostrar cuando hay cambios sin publicar. */
  message?: string;
}

/**
 * Vista del aviso de publicación. Recibe el estado de publicación por props para
 * poder compartir una sola instancia de usePublish con otros controles de la
 * misma página (p. ej. un botón fijo en el encabezado).
 */
export function PublishBannerView({ needsPublish, phase, error, publish, onPublished, message }: ViewProps) {
  const [triggerError, setTriggerError] = useState<string | null>(null);

  useEffect(() => {
    if (phase === 'done') onPublished?.();
  }, [phase, onPublished]);

  // Error al arrancar la publicación (antes del seguimiento que cubre `error`).
  const shownError = error ?? triggerError;

  return (
    <>
      {needsPublish && phase !== 'publishing' && (
        <div className="flex items-start justify-between gap-4 rounded-xl border-l-4 border-l-amber-400 border border-ui-border bg-ui-surface px-4 py-4">
          <div>
            <p className="text-sm font-medium text-ui-text">El cambio aún no es visible en tu sitio</p>
            <p className="text-sm text-ui-text-muted mt-0.5">
              {message ?? 'Publica el sitio para que los visitantes vean los cambios que acabas de guardar.'}
            </p>
            {shownError && <p className="text-sm text-red-400 mt-2">{shownError}</p>}
          </div>
          <PublishButton
            label="Publicar ahora"
            className="shrink-0 rounded-lg bg-amber-400 px-3 py-2 text-sm font-medium text-amber-950 hover:bg-amber-300 disabled:opacity-50 transition-colors"
            onPublish={async () => { setTriggerError(null); await publish(); }}
            onError={() => setTriggerError('No se pudo iniciar la publicación. Inténtalo de nuevo en un momento.')}
          />
        </div>
      )}

      {phase === 'publishing' && (
        <div className="flex items-center gap-3 rounded-xl border-l-4 border-l-sky-400 border border-ui-border bg-ui-surface px-4 py-4">
          <span className="h-2 w-2 shrink-0 rounded-full bg-sky-400 animate-pulse" />
          <div>
            <p className="text-sm font-medium text-ui-text">Estamos publicando tu sitio…</p>
            <p className="text-sm text-ui-text-muted mt-0.5">El cambio aparecerá para los visitantes en uno o dos minutos.</p>
          </div>
        </div>
      )}

      {!needsPublish && phase === 'done' && (
        <div className="rounded-xl border-l-4 border-l-emerald-400 border border-ui-border bg-ui-surface px-4 py-3">
          <p className="text-sm font-medium text-ui-text">Sitio publicado.</p>
          <p className="text-sm text-ui-text-muted mt-0.5">El cambio ya es visible para los visitantes.</p>
        </div>
      )}
    </>
  );
}

interface Props {
  needsPublish: boolean;
  onPublished?: () => void;
  message?: string;
}

/**
 * Aviso reutilizable de "cambios sin publicar". Los cambios guardados en la base
 * de datos no se ven en el sitio hasta publicar (build + deploy). Versión
 * autónoma: monta su propia instancia de usePublish (una activa por ruta).
 * Para compartir estado con otros controles de la página, usar PublishBannerView.
 */
export default function PublishBanner({ needsPublish, onPublished, message }: Props) {
  const { phase, error, publish } = usePublish();
  return (
    <PublishBannerView
      needsPublish={needsPublish}
      phase={phase}
      error={error}
      publish={publish}
      onPublished={onPublished}
      message={message}
    />
  );
}
